import type { BoundingBox } from '@aus-platform/cesium';
import {
  FormMessage,
  FormMessageVariant,
  Icon,
  IconIdentifier,
  Table,
  toast,
} from '@aus-platform/design-system';
import { type RowSelectionState, getCoreRowModel } from '@tanstack/react-table';
import * as turf from '@turf/turf';
import { useAppSelector } from 'app/hooks';
import FileSaver from 'file-saver';
import { compact, isEmpty, isNil, uniqBy } from 'lodash';
import { selectMap3DState } from 'map-3d/shared/map-3d-slices';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  HeapMessageLevel,
  handleResponseErrorMessage,
  useDeleteHeapList,
  useDownloadHeapsReport,
  useFetchHeapClusterList,
  useRenameHeap,
} from 'shared/api';
import { ConfirmationModal } from 'shared/components';
import wkt from 'wkt';
import { flyToCentroid } from '../helpers';
import {
  getHeapCalculationLegend,
  getHeapCalculationMessage,
} from './heap-calculation-message';
import { HeapClusterTableHeader } from './heap-cluster-table-header';
import { heapsClusterListColumnData } from './heaps-cluster-list-column-data';
import { importPolygonWithSVGLabel } from './helpers';
import type { HeapClusterTableProps, HeapsTableData } from './types';

export const HeapClusterTable: React.FC<HeapClusterTableProps> = ({
  setIsHeapsTableVisible,
  heapClustersOptions,
  activeHeapCluster,
  setActiveHeapCluster,
  importWKTPolygon,
  selectedClusters,
  refetchHeapClusterList,
  isHeapClusterModified,
  setIsHeapClusterModified,
  selectedInactiveHeaps,
}) => {
  // States.
  const [isTableExpanded, setIsTableExpanded] = useState<boolean>(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [heapsData, setHeapsData] = useState<HeapsTableData[]>([]);
  const [editingHeapId, setEditingHeapId] = useState<string | null>(null);
  const [isDeleteHeapModalOpen, setIsDeleteHeapModalOpen] =
    useState<boolean>(false);

  // Selectors.
  const { cesiumProxy, aoiManager } = useAppSelector(selectMap3DState);

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;

  // useMemo.
  const selectedHeaps = useMemo(() => {
    return Object.entries(rowSelection)
      .filter(([_, isSelected]) => isSelected)
      .map(([key]) => heapsData.find((heap) => heap.id === key)!)
      .reverse();
  }, [rowSelection, heapsData]);

  // useRefs.
  const heapClusterTableRef = useRef<HTMLInputElement>(null);

  // Apis.
  const {
    data: heapClusterListData,
    isPending: isHeapClusterListLoading,
    isSuccess: isHeapClusterListSuccess,
    isError: isErrorHeapClusterList,
    error: heapClusterListError,
    refetch: refetchAllHeapList,
  } = useFetchHeapClusterList({
    clusterId: activeHeapCluster?.clusterId ?? '',
  });

  const {
    mutate: downloadHeapsReportRequest,
    data: downloadHeapsReportData,
    isSuccess: isDownloadHeapsReportSuccess,
    isError: isDownloadHeapsReportError,
    error: downloadHeapsReportError,
  } = useDownloadHeapsReport();

  const {
    mutate: deleteHeapListRequest,
    isSuccess: isDeleteHeapListSuccess,
    isError: isDeleteHeapListError,
    error: deleteHeapListError,
  } = useDeleteHeapList();

  const {
    mutate: sendRenameHeapRequest,
    isSuccess: isSuccessRenameHeap,
    isError: isErrorRenameHeap,
    error: renameHeapError,
  } = useRenameHeap();

  // useEffects.
  useEffect(() => {
    if (activeHeapCluster) {
      refetchAllHeapList();
    }

    return () => {
      setRowSelection({});
    };
  }, [activeHeapCluster]);

  useEffect(() => {
    if (isHeapClusterModified) {
      refetchAllHeapList();
      setIsHeapClusterModified(false);
    }
  }, [isHeapClusterModified]);

  useEffect(() => {
    if (isNil(polygonTool)) {
      return;
    }

    const unSelectedHeapsList = heapsData.filter(
      (heap) => !rowSelection[heap.id],
    );

    const reorderedHeapsData = [
      ...selectedHeaps,
      ...unSelectedHeapsList,
      ...selectedInactiveHeaps,
    ];

    reorderedHeapsData.forEach((heap) => {
      const {
        heapName,
        id,
        volumeCut,
        volumeFill,
        volumeNet,
        weightCut,
        weightFill,
        weightNet,
        geometry,
      } = heap;

      polygonTool.deleteById(id);
      if (rowSelection[id]) {
        if (
          !volumeCut ||
          !volumeFill ||
          !volumeNet ||
          !weightCut ||
          !weightFill ||
          !weightNet
        ) {
          return;
        }

        importPolygonWithSVGLabel({
          id,
          heapName,
          volumeCut,
          volumeFill,
          volumeNet,
          weightCut,
          weightFill,
          weightNet,
          geometry,
          polygonTool,
        });
      } else {
        importWKTPolygon(heapName, id, geometry);
      }
    });
  }, [selectedHeaps, heapsData]);

  useEffect(() => {
    const coords = selectedHeaps.map((heap) => {
      const { coordinates } = wkt.parse(heap.geometry);
      return coordinates;
    });

    // Buffer to pad the bbox (bounding box) so Cesium doesn't zoom too tightly on geometries.
    const threshold = 0.003;

    if (!isEmpty(coords)) {
      const geometry = turf.multiPolygon(coords);
      const [minX, minY, maxX, maxY] = turf.bbox(geometry);
      const bounds: BoundingBox = [
        minX - threshold,
        minY - threshold,
        maxX + threshold,
        maxY + threshold,
      ];

      cesiumProxy?.reset(bounds);
      return;
    }

    handleZoomToHeaps();
  }, [selectedHeaps]);

  useEffect(() => {
    if (heapClusterListData && isHeapClusterListSuccess) {
      const heapClusterListDataIds = heapClusterListData.heaps.map(
        (heap) => heap.id,
      );

      if (isEmpty(heapClusterListDataIds)) {
        handleCloseTable();
      }

      heapsData.forEach((heap) => {
        if (
          !heapClusterListDataIds.includes(heap.id) &&
          heapClusterListData.clusterId === heap.clusterId
        ) {
          polygonTool?.deleteById(heap.id);
        }
      });

      setHeapsData(
        heapClusterListData.heaps.map((heap) => ({
          id: heap.id,
          heapName: heap.name ?? '-',
          volumeCut: !isNil(heap.cutVolume)
            ? Number(heap.cutVolume).toFixed(2)
            : '-',
          volumeFill: !isNil(heap.fillVolume)
            ? Number(heap.fillVolume).toFixed(2)
            : '-',
          volumeNet: !isNil(heap.netVolume)
            ? Number(heap.netVolume).toFixed(2)
            : '-',
          weightCut: !isNil(heap.cutWeight)
            ? Number(heap.cutWeight).toFixed(2)
            : '-',
          weightFill: !isNil(heap.fillWeight)
            ? Number(heap.fillWeight).toFixed(2)
            : '-',
          weightNet: !isNil(heap.netWeight)
            ? Number(heap.netWeight).toFixed(2)
            : '-',
          status: heap.status,
          geometry: heap.geometry,
          ...getHeapCalculationMessage(heap),
          clusterId: heapClusterListData.clusterId,
        })),
      );
    }
  }, [heapClusterListData, isHeapClusterListSuccess]);

  useEffect(() => {
    if (isErrorHeapClusterList && heapClusterListError) {
      handleResponseErrorMessage(isErrorHeapClusterList, heapClusterListError);
    }
  }, [isErrorHeapClusterList, heapClusterListError]);

  useEffect(() => {
    if (isDownloadHeapsReportError && downloadHeapsReportError) {
      handleResponseErrorMessage(
        isDownloadHeapsReportError,
        downloadHeapsReportError,
      );
    }
  }, [isDownloadHeapsReportError, downloadHeapsReportError]);

  useEffect(() => {
    if (isDeleteHeapListSuccess) {
      setIsDeleteHeapModalOpen(false);
      refetchAllHeapList();
      setRowSelection({});
      refetchHeapClusterList();
    }
  }, [isDeleteHeapListSuccess]);

  useEffect(() => {
    if (isDownloadHeapsReportSuccess && downloadHeapsReportData) {
      const blob = new Blob([downloadHeapsReportData], {
        type: 'text/csv;charset=utf-8;',
      });
      FileSaver.saveAs(
        blob,
        `${activeHeapCluster?.clusterName}_heaps_report.csv`,
      );
    }
  }, [downloadHeapsReportData, isDownloadHeapsReportSuccess]);

  useEffect(() => {
    if (isDeleteHeapListError && deleteHeapListError) {
      handleResponseErrorMessage(isDeleteHeapListError, deleteHeapListError);
    }
  }, [isDeleteHeapListError, deleteHeapListError]);

  useEffect(() => {
    if (isSuccessRenameHeap) {
      refetchAllHeapList();
      refetchHeapClusterList();
    }
  }, [isSuccessRenameHeap]);

  useEffect(() => {
    if (isErrorRenameHeap && renameHeapError) {
      const errorMessage = renameHeapError.meta.details?.['name'][0]['message'];
      toast.error(errorMessage);
    }
  }, [isErrorRenameHeap]);

  // Handlers.
  const closeDeleteHeapModal = () => {
    setIsDeleteHeapModalOpen(false);
  };

  const openDeleteHeapModal = () => {
    setIsDeleteHeapModalOpen(true);
  };

  const getFileRowClassGenerator = (row) => {
    const isSelected = rowSelection[row.id];
    const isEditing = editingHeapId === row.id;

    if (row.messageLevel === HeapMessageLevel.Warning) {
      return 'aus-table-row__warning';
    }

    if (row.messageLevel === HeapMessageLevel.Error) {
      return 'aus-table-row__error';
    }

    if (isEditing) {
      return 'aus-table-row__editing';
    }
    return `aus-table-row${isSelected ? '__checked' : ''}`;
  };

  const handleOnRowMouseClick = (event, row) => {
    if (event.target.closest('.aus-icon-container, .aus-input__txt')) {
      return;
    }

    setRowSelection((prevSelection) => {
      const isSelected = !!prevSelection[row.id];
      if (isSelected) {
        const { [row.id]: _, ...rest } = prevSelection;
        return rest;
      } else {
        return { ...prevSelection, [row.id]: true };
      }
    });
  };

  const handleDownloadSelectedHeaps = () => {
    const heapIds = selectedHeaps.map((heap) => heap.id);
    downloadHeapsReportRequest({
      clusterId: activeHeapCluster!.clusterId,
      heapIds: heapIds,
    });
  };

  const handleDownloadAllHeaps = () => {
    downloadHeapsReportRequest({
      clusterId: activeHeapCluster!.clusterId,
    });
  };

  const handleDeleteHeapList = () => {
    const heapIds = isEmpty(selectedHeaps)
      ? heapsData.map((heap) => heap.id)
      : selectedHeaps.map((heap) => heap.id);

    deleteHeapListRequest({
      clusterId: activeHeapCluster!.clusterId,
      heapIds: heapIds,
    });
  };

  const handleZoomToHeaps = () => {
    const centroid = activeHeapCluster?.clusterId
      ? selectedClusters?.[activeHeapCluster.clusterId]?.centroid
      : null;

    if (centroid && cesiumProxy) {
      flyToCentroid(cesiumProxy.viewer, centroid);
    }
  };

  const handleCloseTable = () => {
    setIsHeapsTableVisible(false);

    heapsData.forEach((heap) => {
      polygonTool?.deleteById(heap.id);
      importWKTPolygon(heap.heapName, heap.id, heap.geometry);
    });

    handleZoomToHeaps();
  };

  const handleRenameHeap = (heapId: string, heapName: string) => {
    sendRenameHeapRequest({
      heapId,
      heapName: heapName,
    });
  };

  const legendEntries = uniqBy(
    compact(
      heapsData.map((heap) =>
        heap.message
          ? {
              text: getHeapCalculationLegend(heap.message),
              messageLevel: heap.messageLevel,
            }
          : null,
      ),
    ),
    'text',
  );

  return (
    <div
      className={`heap-cluster-table ${isTableExpanded ? 'heap-cluster-table__expanded' : 'heap-cluster-table__collapsed'}`}
      ref={heapClusterTableRef}
    >
      <Table
        columns={heapsClusterListColumnData({
          handleRenameHeap,
          editingHeapId,
          setEditingHeapId,
        })}
        data={heapsData}
        getCoreRowModel={getCoreRowModel()}
        enablePaginationFooter={false}
        paginationProps={{
          pageIndex: 0,
          pageSize: heapsData.length,
        }}
        onMouseClick={handleOnRowMouseClick}
        rowClassesGenerator={getFileRowClassGenerator}
        onRowSelectionChange={setRowSelection}
        isLoading={isHeapClusterListLoading}
        rowSelection={rowSelection}
        rowIdentifier="id"
        tableHeaderOptions={
          <HeapClusterTableHeader
            setIsTableExpanded={setIsTableExpanded}
            handleCloseTable={handleCloseTable}
            isTableExpanded={isTableExpanded}
            heapClustersOptions={heapClustersOptions}
            setActiveHeapCluster={setActiveHeapCluster}
            activeHeapCluster={activeHeapCluster}
            openDeleteHeapModal={openDeleteHeapModal}
            selectedHeaps={selectedHeaps}
            handleDownloadSelectedHeaps={handleDownloadSelectedHeaps}
            handleDownloadAllHeaps={handleDownloadAllHeaps}
          />
        }
      />

      {!isEmpty(legendEntries) && (
        <div className="heap-cluster-table__legend">
          {legendEntries.map((entry) => (
            <div
              key={entry.text}
              className={`heap-cluster-table__legend-item heap-cluster-table__legend-item--${entry.messageLevel}`}
            >
              <span className="heap-cluster-table__legend-square-box" />
              <span className="heap-cluster-table__legend-text">
                {entry.text}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* modals */}
      {isDeleteHeapModalOpen && (
        <ConfirmationModal
          isConfirmDanger
          title="Delete Confirmation"
          message={
            <>
              <div className="modal-body__message">
                {isEmpty(selectedHeaps) ? (
                  <span>Are you sure you want to delete all heaps?</span>
                ) : (
                  <>
                    <span>{'Are you sure you want to delete the heap '}</span>
                    <strong>
                      {selectedHeaps.map((heap) => heap?.heapName).join(', ')}
                    </strong>
                    <span>?</span>
                  </>
                )}
              </div>

              <FormMessage
                variant={
                  selectedHeaps.length === heapsData.length ||
                  isEmpty(selectedHeaps)
                    ? FormMessageVariant.Warning
                    : FormMessageVariant.Info
                }
                message={
                  selectedHeaps.length === heapsData.length ||
                  isEmpty(selectedHeaps) ? (
                    <>
                      <Icon identifier={IconIdentifier.Warning} size={15} />
                      Deleting all heaps will delete the cluster too
                    </>
                  ) : (
                    'Deleting heap will recalculate cumulative volume & weight'
                  )
                }
              />
            </>
          }
          containerRef={heapClusterTableRef}
          confirmText="Delete"
          onSubmit={handleDeleteHeapList}
          onClose={closeDeleteHeapModal}
          className="delete-heap-modal"
        />
      )}
    </div>
  );
};
